import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '@/lib/utils';

/**
 * Design system §6. Mono 11, radius 6, tint fill — e.g. `2 edits`, `saved`, `scanned`.
 * Signals are text + tint + 1px border, never a full-width filled banner.
 */
const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-sm border px-1.5 py-0.5 font-mono text-[11px] leading-[1.3] whitespace-nowrap transition-colors duration-(--animate-duration-hover) ease-(--ease-standard) focus-visible:ring-focus [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-accent text-accent-foreground',
        success: 'border-success-border bg-success-tint text-success',
        caution: 'border-caution-border bg-caution-tint text-caution',
        destructive: 'border-destructive-border bg-destructive-tint text-destructive',
        outline: 'border-border bg-card text-ink-2',
        /** Marketing tag on a plan card: ink on teal, uppercase mono 10.5. */
        popular: 'border-transparent bg-primary text-[10.5px] tracking-[0.08em] text-ink uppercase',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Badge({
  className,
  variant = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span';

  return (
    <Comp data-slot="badge" data-variant={variant} className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
